/* eslint-disable no-shadow */
import React, { useState } from 'react';
import mammoth from 'mammoth';
import { useTranslation } from 'react-i18next';
import { DialogContent } from '@mui/material';
import Dialog from '@src/components/Dialog';
import NewFileDropzone from '@src/components/NewFileDropzone';
import { useSelector } from 'react-redux';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import Tesseract from 'tesseract.js';
import { franc } from 'franc-min';
import {
  ALOWED_CHARACTERS_PDF_REGEX,
  OPTION_UPLOAD_FILE,
} from '@src/constants/upload';
import { PACKAGE_STUDIO_VERSION } from '@src/constants/package';
import {
  checkFeaturePermission,
  countImportedContentLength,
} from '@src/services/tts';
import { trackEvent } from '@src/services/moengage';
import {
  MOE_EVENTS,
  MOE_DOCUMENT_TYPE,
  MOE_PROJECT_SOURCE_TYPE,
} from '@src/constants/moengage';
import useUserInfo from '@src/hooks/useTtsUser';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';

// Add the workerSrc for pdfjs
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.js`;

const LANGUGE_VN = 'vie';
const TYPE_FILE = {
  TEXT: 'text/plain',
  PDF: 'application/pdf',
};

const UploadFile = ({ open, onClose, onHandleUploadContent }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const [loadingGetContentFile, setLoadingGetContentFile] = useState(false);

  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();

  const newUiProjectsEnabled = getFeatureValue(FEATURE_KEYS.NEW_UI_PROJECTS, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  let optionUploadFile = OPTION_UPLOAD_FILE;

  const isPackageV2 =
    usingPackage?.version === PACKAGE_STUDIO_VERSION.V2 ||
    usingPackage?.version === PACKAGE_STUDIO_VERSION.V2_1;

  if (isPackageV2) {
    optionUploadFile = optionUploadFile.filter((item) =>
      checkFeaturePermission(ttsUser?.features, item.feature),
    );
  }

  const fileType = optionUploadFile
    .filter((item) => item.isActive)
    .map((item) => item.type)
    .flat();

  function replaceMultipleSpacesWithNewline(inputString) {
    const MULTIPLE_SPACES_REGEX = / {3,}/g;
    return inputString.replace(MULTIPLE_SPACES_REGEX, '\n');
  }

  function replaceDoubleSpaces(str) {
    return str.replace(/ {2,}/g, ' ');
  }

  const checkStrMoreThanTwoSpaces = (str) => str.split(' ').length > 2;

  const getTextContentPdf = async (pages) => {
    const textContentPromises = pages.map((page) => page.getTextContent());
    const textContents = await Promise.all(textContentPromises);

    // Check srt in the first item of textContents to determine if the pdf has more than two spaces
    // If the pdf has more than two spaces, join all items in textContents without space
    // Otherwise, join all items in textContents with space
    const srtMoreThanTwoSpaces = textContents[0].items.some((item) =>
      checkStrMoreThanTwoSpaces(item.str),
    );

    const text = textContents
      .flatMap((textContent) => textContent.items)
      .map((item) => item.str)
      .join(srtMoreThanTwoSpaces ? '' : ' ');

    // Get the language of the first 100 characters
    const first100Chars = text.substring(0, 100);
    const language = franc(first100Chars);

    // If the language is Vietnamese, replace multiple spaces with newline
    if (language === LANGUGE_VN) {
      const textWithNewline = replaceMultipleSpacesWithNewline(text);
      const textReplaceDoubleSpaces = replaceDoubleSpaces(textWithNewline);
      return textReplaceDoubleSpaces;
    }

    return text;
  };

  const getTextContentPdfScanned = async (pages) => {
    const textContentScannedPromises = pages.map(async (page) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      const viewport = page.getViewport({ scale: 2 });
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      await page.render({ canvasContext: context, viewport }).promise;

      const dataUrl = canvas.toDataURL();
      return Tesseract.recognize(dataUrl, LANGUGE_VN, {
        // eslint-disable-next-line
        logger: (m) => console.log(m),
      });
    });

    const textContentsScanned = await Promise.all(textContentScannedPromises);

    return textContentsScanned.map((result) => result.data.text).join(' ');
  };

  const isErrorCharacter = (char) => {
    if (char === '\n') {
      return false;
    }
    if (char === '\r') {
      return false;
    }
    return !ALOWED_CHARACTERS_PDF_REGEX.test(char);
  };

  const checkPdfIsScanned = (text) => {
    // If uploadContent is empty, it means the pdf is scanned
    if (!text?.trim()) return true;

    const errorCount = [...text].filter((char) =>
      isErrorCharacter(char),
    ).length;

    // If the error rate is greater than 5%, it means the pdf is scanned
    const threshold = 5;
    const errorRate = (errorCount / text.length) * 100;

    return errorRate > threshold;
  };

  const trackDocumentImported = (documentType, uploadContent) =>
    trackEvent(MOE_EVENTS.DOCUMENT_IMPORTED, {
      document_type: documentType,
      content_length: countImportedContentLength(uploadContent),
    });

  const handleAddFile = (newFile, handleReset) => {
    setLoadingGetContentFile(true);
    const reader = new FileReader();
    const fileType = newFile.type;
    // TODO: Change logic
    switch (fileType) {
      // Handle text file
      case TYPE_FILE.TEXT: {
        reader.onloadend = async () => {
          const uploadContent = reader.result;
          await onHandleUploadContent(
            uploadContent,
            MOE_PROJECT_SOURCE_TYPE.FILE_TXT,
          );
          trackDocumentImported(MOE_DOCUMENT_TYPE.TXT, uploadContent, newFile);
          setLoadingGetContentFile(false);
          onClose();
          handleReset();
        };
        reader.readAsText(newFile);

        break;
      }
      // Handle pdf file
      case TYPE_FILE.PDF: {
        reader.onloadend = async () => {
          const arrayBuffer = reader.result;

          // Load the pdf
          const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;

          // Get the number of pages
          const numPages = await pdf.numPages;

          // Get the text content of each page
          const pagePromises = Array.from({ length: numPages }, (_, i) =>
            pdf.getPage(i + 1),
          );
          const pages = await Promise.all(pagePromises);

          let uploadContent = '';

          // Get the text content of each page
          uploadContent = await getTextContentPdf(pages);

          // Check if the pdf is scanned
          const isScanned = checkPdfIsScanned(uploadContent);

          if (isScanned) {
            // Get the text content of each page for scanned pdf
            uploadContent = await getTextContentPdfScanned(pages);
          }
          await onHandleUploadContent(
            uploadContent,
            MOE_PROJECT_SOURCE_TYPE.FILE_PDF,
          );
          trackDocumentImported(
            isScanned ? MOE_DOCUMENT_TYPE.PDF_SCANNED : MOE_DOCUMENT_TYPE.PDF,
            uploadContent,
            newFile,
          );
          setLoadingGetContentFile(false);
          onClose();
          handleReset();
        };
        reader.readAsArrayBuffer(newFile);

        break;
      }
      // Handle docx file
      default: {
        reader.onloadend = async () => {
          const arrayBuffer = reader.result;
          const { value: uploadContent } = await mammoth.extractRawText({
            arrayBuffer,
          });
          await onHandleUploadContent(
            uploadContent,
            MOE_PROJECT_SOURCE_TYPE.FILE_DOC,
          );
          trackDocumentImported(MOE_DOCUMENT_TYPE.DOCX, uploadContent, newFile);
          setLoadingGetContentFile(false);
          onClose();
          handleReset();
        };
        reader.readAsArrayBuffer(newFile);

        break;
      }
    }
  };

  if (newUiProjectsEnabled) {
    return (
      <CommonDialog
        open={open}
        onClose={onClose}
        title={t('uploadYourFile')}
        disableAutoClose
        width="640px"
        contentComponent={
          <NewFileDropzone
            fileType={fileType}
            onAddFile={handleAddFile}
            loadingGetContentFile={loadingGetContentFile}
            dataId="import-via-file"
            newUiProjectsEnabled
          />
        }
      />
    );
  }

  return (
    <Dialog
      title={t('uploadYourFile')}
      open={open}
      fullWidth
      dataAttribute={{ 'data-id': 'close-import-via-file' }}
      onClose={onClose}
    >
      <DialogContent
        dividers
        sx={{
          '&.MuiDialogContent-root': {
            paddingTop: '24px !important',
          },
        }}
      >
        <NewFileDropzone
          fileType={fileType}
          onAddFile={handleAddFile}
          loadingGetContentFile={loadingGetContentFile}
          dataId="import-via-file"
        />
      </DialogContent>
    </Dialog>
  );
};

export default UploadFile;
